import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SupabaseAuthProvider, useAuth } from "@/lib/supabase-auth";
import { getRedirectPath } from "@/lib/rbac";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import SystemStatus from "@/components/SystemStatus";
import { DevUserSwitcher } from "@/components/DevUserSwitcher";
import ApiDashboard from "@/components/ApiDashboard";
import ApiTestPage from "@/components/ApiTestPage";
import { ClarityProvider } from "@/components/ClarityProvider";

// Pages
import React, { Suspense, lazy } from 'react';
const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Eventos = lazy(() => import('./pages/Eventos'));
const EventEdit = lazy(() => import('./pages/EventEdit').then(m => ({ default: m.EventEdit })));
const MinhaAgenda = lazy(() => import('./pages/MinhaAgenda'));
const AgendaCompleta = lazy(() => import('./pages/AgendaCompleta'));
const Painel = lazy(() => import('./pages/Painel'));
const Mapa = lazy(() => import('./pages/Mapa'));
const Usuarios = lazy(() => import('./pages/Usuarios'));
const Mensagens = lazy(() => import('./pages/Mensagens'));
const Branding = lazy(() => import('./pages/Branding'));
const Participantes = lazy(() => import('./pages/Participantes'));
const SchedulerFabricantesPage = lazy(() => import('./pages/SchedulerFabricantesPage'));
const AgendaSwitcherPage = lazy(() => import('./pages/AgendaSwitcherPage'));
const ConfiguracaoAssociacoes = lazy(() => import('./pages/ConfiguracaoAssociacoes'));
const AjustesHorarios = lazy(() => import('./pages/AjustesHorarios'));
const NotFound = lazy(() => import('./pages/NotFound'));
const UnassignedRevendasPage = lazy(() => import('./pages/UnassignedRevendasPage'));

const queryClient = new QueryClient();

// Componente para redirecionamento baseado em papel
function RoleBasedRedirect() {
  const { user, isAuthenticated } = useAuth();
  
  console.log('🔄 [RoleBasedRedirect] Estado atual:', { user, isAuthenticated });
  
  if (!isAuthenticated) {
    console.log('❌ [RoleBasedRedirect] Usuário não autenticado, redirecionando para login');
    return <Navigate to="/login" replace />;
  }
  
  if (user) {
    const redirectPath = getRedirectPath(user.papel);
    console.log('✅ [RoleBasedRedirect] Redirecionando usuário para:', redirectPath, 'papel:', user.papel);
    return <Navigate to={redirectPath} replace />;
  }
  
  console.log('⚠️ [RoleBasedRedirect] Estado inconsistente: autenticado mas sem usuário, redirecionando para login');
  return <Navigate to="/login" replace />;
}

const Fallback = () => (
  <div className="flex items-center justify-center w-full h-[50vh]" role="status" aria-live="polite">
    <div className="flex flex-col items-center gap-4 text-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-b2b-primary border-t-transparent" />
      <p className="text-sm text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

const App = () => {
  // Construir rotas dinamicamente baseado em feature flags
  const routes = [
    { path: "/login", element: <Login /> },
    { path: "/auth/callback", element: <AuthCallback /> },
    { path: "/", element: <RoleBasedRedirect /> },
    
    // Rotas protegidas com controle de acesso específico para cada papel
    { 
      path: "/eventos", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <Eventos />
        </ProtectedRoute>
      )
    },
    { 
      path: "/eventos/:eventId/edit", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <EventEdit />
        </ProtectedRoute>
      )
    },
    { 
      path: "/eventos/:eventId/agenda", 
      element: (
        <ProtectedRoute allowedRoles={['AGIS', 'PRODUCAO']}>
          <AgendaCompleta />
        </ProtectedRoute>
      )
    },
    { 
      path: "/participantes", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <Participantes />
        </ProtectedRoute>
      )
    },
    { 
      path: "/minha-agenda", 
      element: (
        <ProtectedRoute allowedRoles={['FABRICANTE', 'REVENDA', 'PRODUCAO']}>
          <MinhaAgenda />
        </ProtectedRoute>
      )
    },
    { 
      path: "/painel", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO', 'AGIS']}>
          <Painel />
        </ProtectedRoute>
      )
    },
    { 
      path: "/mapa", 
      element: (
        <ProtectedRoute allowedRoles={['AGIS', 'PRODUCAO', 'FABRICANTE', 'REVENDA']}>
          <Mapa />
        </ProtectedRoute>
      )
    },
    { 
      path: "/usuarios", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <Usuarios />
        </ProtectedRoute>
      )
    },
    { 
      path: "/mensagens", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <Mensagens />
        </ProtectedRoute>
      )
    },
    { 
      path: "/branding", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <Branding />
        </ProtectedRoute>
      )
    },
    { 
      path: "/configuracao/associacoes", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <ConfiguracaoAssociacoes />
        </ProtectedRoute>
      )
    },
    { 
      path: "/ajustes-horarios", 
      element: (
        <ProtectedRoute allowedRoles={['PRODUCAO']}>
          <AjustesHorarios />
        </ProtectedRoute>
      )
    },   
    
    // API Dashboard - Frontend Integration Demo
    { path: "/api-dashboard", element: <ApiDashboard /> },
    
    // API Test Page - CRUD Operations Demo
    { path: "/api-test", element: <ApiTestPage /> },
    
    // Catch-all para 404
    { path: "*", element: <NotFound /> }
  ];


  // Adicionar rota condicional do Scheduler Fabricantes
  if (import.meta.env.VITE_FEATURE_FABRICANTES_SCHEDULER === 'true') {
    routes.splice(-1, 0, { 
      path: "/scheduler-fabricantes", 
      element: (
        <ProtectedRoute allowedRoles={['FABRICANTE', 'PRODUCAO']}>
          <SchedulerFabricantesPage />
        </ProtectedRoute>
      )
    });
  }

  // Adicionar rota para o seletor de agenda
  routes.splice(-1, 0, { 
    path: "/agenda-switcher", 
    element: (
      <ProtectedRoute allowedRoles={['AGIS', 'PRODUCAO']}>
        <AgendaSwitcherPage />
      </ProtectedRoute>
    )
  });

  // Adicionar rota para a página de participantes REVENDA não associados
  routes.splice(-1, 0, { 
    path: "/eventos/:eventId/unassigned-revendas", 
    element: (
      <ProtectedRoute allowedRoles={['PRODUCAO']}>
        <UnassignedRevendasPage />
      </ProtectedRoute>
    )
  });

  return (
    <QueryClientProvider client={queryClient}>
      <SupabaseAuthProvider>
        <ClarityProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            {/* <SystemStatus /> */}
            {/* <DevUserSwitcher /> */}
            <BrowserRouter>
              <Suspense fallback={<Fallback />}>
              <Routes>
                {routes.map((route, index) => (
                  <Route key={index} path={route.path} element={route.element} />
                ))}
              </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>  
        </ClarityProvider>
      </SupabaseAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
